import { CardPlaceholder, type Product } from '@bucketplace/common';
import {
  FONT_STYLE,
  MEDIA_QUERY,
  ResizeImage,
  useResponsive,
} from '@bucketplace/design-system';
import { Grid, GridItem } from '@bucketplace/design-system/bds';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Fragment, type ReactElement } from 'react';
import { ProductCardSectionItem } from '@/components/Product/ProductCardSectionItem';
import { useUserContext } from '@/provider';
import { useTRPC } from '@/trpc/utils';
import { isMobile as isMobileDevice } from '@/utils';
import { InfiniteScroll } from './InfiniteScroll';

const LIST_SIZE = 20;

interface ExhibitionSectionItemsProps {
  id: string;
  detailId: string;
  sectionId: string;
}

export function ExhibitionSectionItems({
  id,
  detailId,
  sectionId,
}: ExhibitionSectionItemsProps): ReactElement | null {
  const { userInfo } = useUserContext();
  const isLoggedIn = !!userInfo?.user;

  const trpc = useTRPC();
  const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery(
    trpc.promotions.getExhibitionSectionItems.infiniteQueryOptions(
      {
        exhibitionId: id,
        detailId,
        sectionId,
        isLoggedIn,
      },
      {
        initialCursor: 0,
        getNextPageParam: (lastPage, _, lastPageParam) =>
          lastPage.hasNext && lastPageParam !== null ? lastPageParam + 1 : null,
      },
    ),
  );

  const { breakpoint } = useResponsive({});
  const isMobile = breakpoint === 'mobile';
  const flatItems = data?.pages.flatMap((page) => page.sectionItems);

  const sectionItems = (flatItems ?? []).filter(
    (item) => item.item?.isSelling && !item.item.isDiscontinued,
  );

  if (data == null) {
    return (
      <>
        {Array.from({ length: LIST_SIZE }).map((_, index) => (
          <StyledGridItem
            key={`${index + 1}`}
            column={{
              mobile: 'half',
              tablet: 'half',
              laptop: 4,
            }}
          >
            <CardPlaceholder />
          </StyledGridItem>
        ))}
      </>
    );
  }

  return (
    <InfiniteScroll
      onLoad={() => {
        fetchNextPage();
      }}
      hasMore={hasNextPage}
      pending={isFetching}
      tagName="ul"
    >
      <SectionItems
        gap={{
          mobile: '0 16',
          tablet: '30 16',
          laptop: '30 20',
        }}
      >
        {sectionItems?.map((sectionItem, index) => {
          const imageUrl =
            isMobile || isMobileDevice()
              ? sectionItem.mobileImageUrl
              : sectionItem.pcImageUrl;
          const isFirstItem = index === 0;
          const isFirstSectionItem =
            index > 0 && sectionItem.id !== sectionItems[index - 1]?.id;

          return (
            <Fragment key={`${sectionItem.item?.id}_${index + 1}`}>
              {(isFirstItem || isFirstSectionItem) &&
                (sectionItem.title || imageUrl) && (
                  <StyledGridItem
                    column={{
                      mobile: 'full',
                      tablet: 'full',
                      laptop: 'full',
                    }}
                  >
                    <a
                      {...(sectionItem?.linkUrl
                        ? { href: sectionItem.linkUrl }
                        : {})}
                    >
                      {imageUrl ? (
                        <BannerImage src={imageUrl} alt="섹션 배너" />
                      ) : (
                        <Title>{sectionItem.title}</Title>
                      )}
                    </a>
                  </StyledGridItem>
                )}
              <StyledGridItem
                column={{
                  mobile: 'half',
                  tablet: 'half',
                  laptop: 4,
                }}
              >
                <ProductCardSectionItem
                  css={{
                    padding: '15px 0',
                  }}
                  product={sectionItem.item as Product}
                  logData={{
                    section: '기획전 디테일',
                    index,
                  }}
                />
              </StyledGridItem>
            </Fragment>
          );
        })}
      </SectionItems>
    </InfiniteScroll>
  );
}

const SectionItems = styled(Grid)`
  ${MEDIA_QUERY.max(
    'tablet',
    `
    padding: 0 16px;
  `,
  )}
`;

const StyledGridItem = styled(GridItem)`
  &:first-of-type {
    > p {
      margin: 0 -10px -10px;
      ${MEDIA_QUERY.max(
        'tablet',
        css`
        margin: 0px -16px 4px;
      `,
      )}
    }
  }
  &:not(:first-of-type) {
    > p {
      margin: 40px -10px -10px;
      ${MEDIA_QUERY.max(
        'tablet',
        css`
        margin: 24px -16px 4px;
      `,
      )}
    }
  }
`;

const Title = styled.p`
  ${FONT_STYLE.title1}
  padding: 34px 0;
  background-color: ${({ theme }) => theme.content.base5};
  text-align: center;
  font-weight: 700;
  ${({ theme }) =>
    MEDIA_QUERY.max(
      'tablet',
      css`
    ${FONT_STYLE.title2}
    padding: 20px 0;
    background-color: ${theme.content.base4};
  `,
    )}
`;

const BannerImage = styled(ResizeImage)`
  width: 100%;
  display: block;
  margin: 0 0 20px;
`;
